import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarDays, ShieldAlert, Users, Bell, ArrowUpRight, AlarmClockPlus } from "lucide-react";
import { Link } from "react-router-dom";
import StaffProfile from "@/components/StaffProfile";
import ComplianceScore from "@/components/ComplianceScore";

function Stat({ label, value, icon: Icon, tone = "default", testId, onClick }) {
  const tones = {
    default: "bg-white",
    warn: "bg-[#FFF8E6] border-[#FFE4A0]",
    danger: "compliance-expired",
    ok: "compliance-valid",
  };
  return (
    <div className={`accp-card p-6 ${tones[tone]} ${onClick ? "cursor-pointer hover:-translate-y-0.5 transition" : ""}`}
         onClick={onClick} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="accp-label">{label}</div>
          <div className="text-4xl font-semibold text-primary mt-2" style={{fontFamily:'Outfit'}}>{value}</div>
        </div>
        <Icon size={22} className="text-muted-foreground" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [profileId, setProfileId] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const c = data.counts;
  const criticalOpen = c.critical_incidents > 0;

  const handleAlertClick = (a) => {
    if (a.incident_id) nav("/incidents");
    else if (a.shift_id) nav("/shifts");
    else if (a.user_id && user.role === "admin") setProfileId(a.user_id);
  };

  return (
    <div className="space-y-8">
      {criticalOpen && (
        <div className="rounded-2xl px-5 py-4 compliance-expired flex items-center justify-between shadow-sm"
             data-testid="critical-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle size={20}/>
            <div>
              <div className="font-semibold" style={{fontFamily:'Outfit'}}>
                {c.critical_incidents} critical incident{c.critical_incidents>1?"s":""} awaiting review
              </div>
              <div className="text-xs opacity-90">Admin has been notified via email (mocked). Please review immediately.</div>
            </div>
          </div>
          <Link to="/incidents" className="text-sm font-semibold underline">Open incidents →</Link>
        </div>
      )}

      <div>
        <div className="accp-label">Operations overview</div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-primary" style={{fontFamily:'Outfit'}}>
          Hello, {user.name.split(" ")[0]}.
        </h1>
        <p className="text-muted-foreground mt-1">
          {user.role === "dsp"
            ? "Your day at a glance — clock in, review meds, stay compliant."
            : "Your live compliance & workforce snapshot."}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label="Staff" value={c.staff} icon={Users} testId="stat-staff" onClick={user.role==="admin"?()=>nav("/staff"):null}/>
        <Stat label="Shifts today" value={c.shifts_today} icon={CalendarDays} testId="stat-shifts-today" onClick={()=>nav("/shifts")}/>
        <Stat label="Expired certs" value={c.expired_certs} icon={ShieldAlert}
              tone={c.expired_certs>0?"danger":"default"} testId="stat-expired" onClick={()=>nav("/certifications")}/>
        <Stat label="Expiring < 30d" value={c.expiring_certs} icon={AlertTriangle}
              tone={c.expiring_certs>0?"warn":"default"} testId="stat-expiring" onClick={()=>nav("/certifications")}/>
        <Stat label="OT warn ≥ 36h" value={c.ot_warn} icon={AlarmClockPlus}
              tone={c.ot_warn>0?"warn":"default"} testId="stat-ot-warn"/>
        <Stat label="OT critical ≥ 40h" value={c.ot_critical} icon={AlarmClockPlus}
              tone={c.ot_critical>0?"danger":"default"} testId="stat-ot-critical"/>
      </div>

      {user.role === "admin" && <ComplianceScore />}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 accp-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="accp-label">Live alerts</div>
              <h3 className="text-xl font-semibold text-primary mt-1" style={{fontFamily:'Outfit'}}>Compliance & staffing</h3>
            </div>
            <Link to="/notifications" className="text-sm text-primary hover:underline flex items-center gap-1">
              View all ({data.alert_count}) <ArrowUpRight size={14}/>
            </Link>
          </div>
          {data.alerts.length === 0 ? (
            <div className="rounded-xl compliance-valid p-4 text-sm">All clear. No open alerts.</div>
          ) : (
            <ul className="space-y-2">
              {data.alerts.map((a) => (
                <li
                  key={a.id}
                  onClick={() => handleAlertClick(a)}
                  className={`rounded-xl px-4 py-3 flex items-start justify-between gap-4 cursor-pointer hover:brightness-95 transition ${
                    a.severity==="high" ? "compliance-expired" : "bg-[#FFF8E6] border border-[#FFE4A0] text-[#7A5A00]"
                  }`}
                  title="Click to open"
                  data-testid={`alert-${a.type}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{a.title}</div>
                    <div className="text-xs opacity-90 truncate">{a.message}</div>
                    {(a.staff_code || a.shift_code || a.incident_code) && (
                      <div className="text-[10px] font-mono opacity-70 mt-0.5">
                        {[a.staff_code, a.shift_code, a.incident_code].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] uppercase tracking-wider font-bold shrink-0">{a.type.replace(/_/g," ")}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="accp-card p-6">
          <div className="accp-label">Compliance rulebook</div>
          <h3 className="text-xl font-semibold text-primary mt-1 mb-4" style={{fontFamily:'Outfit'}}>Hard locks</h3>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li><span className="font-semibold text-primary">Schedule/edit:</span> Requires valid CPR + First Aid for every applicable participant shift. Medication shifts additionally require CMT + RN Delegation. Any cert expiring during the shift blocks save.</li>
            <li><span className="font-semibold text-primary">Overlap:</span> Two shifts for the same staff cannot overlap in time.</li>
            <li><span className="font-semibold text-primary">Clock-in:</span> Assigned to shift + valid credentials + within 200 ft of the site.</li>
            <li><span className="font-semibold text-primary">Alerts:</span> 30d before expiry · 15 min late clock-in · OT warn ≥ 36h · OT critical ≥ 40h.</li>
          </ul>
        </div>
      </div>

      {profileId && <StaffProfile userId={profileId} onClose={()=>setProfileId(null)} onRefresh={load}/>}
    </div>
  );
}
